<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center space-y-2">
      <h2 class="text-2xl font-bold text-foreground">
        {{ $t('vet.signup.petDetails.title') }}
      </h2>
      <p class="text-muted-foreground">
        {{ $t('vet.signup.petDetails.description') }}
      </p>
    </div>

    <!-- Form Fields -->
    <div class="space-y-4">
      <!-- Pet Name (Required) -->
      <div class="space-y-2">
        <UiLabel for="petName">
          {{ $t('vet.signup.petDetails.name') }}
          <span class="text-destructive">*</span>
        </UiLabel>
        <UiInput
          id="petName"
          v-model="petName"
          type="text"
          :placeholder="$ts('vet.signup.petDetails.namePlaceholder')"
          @input="handleInput"
        />
        <p v-if="showNameError" class="text-sm text-destructive">
          {{ $t('vet.signup.petDetails.nameRequired') }}
        </p>
      </div>

      <!-- Age and Weight Row -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Age -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <UiLabel for="petAge">{{ $t('vet.signup.petDetails.age') }}</UiLabel>
            <span class="text-xs text-muted-foreground">{{ $t('vet.signup.petDetails.ageOptional') }}</span>
          </div>
          <UiInput
            id="petAge"
            v-model.number="petAge"
            type="number"
            min="0"
            max="50"
            step="0.5"
            :placeholder="$ts('vet.signup.petDetails.agePlaceholder')"
            @input="handleInput"
          />
        </div>

        <!-- Weight -->
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <UiLabel for="petWeight">{{ $t('vet.signup.petDetails.weight') }}</UiLabel>
            <span class="text-xs text-muted-foreground">{{ $t('vet.signup.petDetails.weightOptional') }}</span>
          </div>
          <UiInput
            id="petWeight"
            v-model.number="petWeight"
            type="number"
            min="0"
            max="500"
            step="0.1"
            :placeholder="$ts('vet.signup.petDetails.weightPlaceholder')"
            @input="handleInput"
          />
        </div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="flex justify-between pt-4">
      <UiButton variant="outline" @click="$emit('back')">
        <Icon name="tabler:arrow-left" size="16" />
        {{ $t('vet.signup.navigation.back') }}
      </UiButton>
      <UiButton
        :disabled="!isValid"
        class="bg-bubba-blue hover:bg-bubba-blue/90"
        @click="handleNext"
      >
        {{ $t('vet.signup.navigation.next') }}
        <Icon name="tabler:arrow-right" size="16" />
      </UiButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { SignupFormData } from '#composables/vet/useVetSignupWizard';

const props = defineProps<{
  formData: SignupFormData;
}>();

const emit = defineEmits<{
  update: [data: Partial<SignupFormData>];
  next: [];
  back: [];
}>();

const petName = ref(props.formData.petName || '');
const petAge = ref<number | undefined>(props.formData.petAge);
const petWeight = ref<number | undefined>(props.formData.petWeight);
const touched = ref(false);

const showNameError = computed(() => {
  return touched.value && !petName.value.trim();
});

const isValid = computed(() => {
  return petName.value.trim().length > 0;
});

function handleInput() {
  touched.value = true;
  emit('update', {
    petName: petName.value,
    petAge: petAge.value || undefined,
    petWeight: petWeight.value || undefined,
  });
}

function handleNext() {
  touched.value = true;
  if (isValid.value) {
    emit('next');
  }
}
</script>
